import React, { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../services/api";
import { ICE_SERVERS } from "./supportIce";

function rtcWsUrl(machineId) {
  const http = String(API_BASE_URL || "").replace(/\/$/, "");
  const ws = http.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
  const token = localStorage.getItem("token") || "";
  return `${ws}/support-rtc?role=operator&token=${encodeURIComponent(token)}&machine_id=${machineId}`;
}

export default function SupportVideoCall({ machineId, ticketId, initialAudioStream }) {
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);
  const initialAudioRef = useRef(initialAudioStream);
  initialAudioRef.current = initialAudioStream;
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [needClick, setNeedClick] = useState(false);
  const [hasRemote, setHasRemote] = useState(false);

  useEffect(() => {
    if (!machineId) {
      return undefined;
    }
    let cancelled = false;
    let remoteReady = false;
    const pendingIce = [];
    const pc = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
      iceCandidatePoolSize: 2
    });
    pcRef.current = pc;

    function attachRemote(stream) {
      const el = remoteRef.current;
      if (!el || !stream) {
        return;
      }
      el.srcObject = stream;
      el.muted = true;
      const play = el.play();
      if (play && typeof play.catch === "function") {
        play.catch(() => {
          if (!cancelled) setNeedClick(true);
        });
      }
      if (!cancelled) {
        setHasRemote(true);
        setNeedClick(true);
      }
    }

    pc.ontrack = ev => {
      if (ev.streams && ev.streams[0]) {
        attachRemote(ev.streams[0]);
        return;
      }
      const el = remoteRef.current;
      const stream =
        (el && el.srcObject) || new MediaStream(ev.track ? [ev.track] : []);
      if (ev.track && !stream.getTracks().includes(ev.track)) {
        stream.addTrack(ev.track);
      }
      attachRemote(stream);
    };
    pc.onconnectionstatechange = () => {
      if (!cancelled) setStatus(pc.connectionState || "connecting");
    };
    pc.oniceconnectionstatechange = () => {
      if (cancelled || !pc.iceConnectionState) {
        return;
      }
      const ice = pc.iceConnectionState;
      if (ice === "failed" || ice === "disconnected") {
        setError("No se pudo enlazar audio/video. Colgá y volvé a iniciar.");
      }
      setStatus(ice);
    };

    const ws = new WebSocket(rtcWsUrl(machineId));
    wsRef.current = ws;

    function send(obj) {
      if (ws.readyState === 1) {
        ws.send(JSON.stringify(obj));
      }
    }

    pc.onicecandidate = ev => {
      if (ev.candidate) {
        send({ type: "ice", candidate: ev.candidate.toJSON() });
      }
    };

    async function ensureMic() {
      if (localStreamRef.current) return localStreamRef.current;
      const held = initialAudioRef.current;
      const existing = held && held.active ? held : null;
      const stream =
        existing ||
        (await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false
        }));
      localStreamRef.current = stream;
      stream.getTracks().forEach(t => {
        if (pc.getSenders().some(s => s.track === t)) return;
        pc.addTrack(t, stream);
      });
      return stream;
    }

    async function flushIce() {
      while (pendingIce.length) {
        const candidate = pendingIce.shift();
        try {
          await pc.addIceCandidate(candidate);
        } catch (_) {
          /* ignore */
        }
      }
    }

    ensureMic().catch(() => {
      if (!cancelled) {
        setError("Permití el micrófono del navegador para hablar con el cliente");
      }
    });

    const keepalive = setInterval(() => {
      if (ws.readyState === 1) {
        send({ type: "ready" });
      }
    }, 20000);

    ws.onopen = () => {
      if (!cancelled) setStatus("esperando al celular de la máquina…");
      send({ type: "ready" });
    };
    ws.onerror = () => {
      if (!cancelled) setError("No se pudo abrir el canal de videollamada");
    };
    ws.onmessage = async ev => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch (_) {
        return;
      }
      if (msg.type === "error") {
        setError(msg.error || "Error de señalización");
        return;
      }
      if (msg.type === "peer-joined" && msg.role === "device") {
        send({ type: "ready" });
        setStatus("celular conectado, negociando…");
      }
      if (msg.type === "offer" && msg.sdp) {
        try {
          await ensureMic().catch(() => null);
          await pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
          remoteReady = true;
          await flushIce();
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: "answer", sdp: answer.sdp });
          setStatus("conectando audio/video…");
        } catch (e) {
          setError(e.message || "Fallo al aceptar la llamada");
        }
      }
      if (msg.type === "ice" && msg.candidate) {
        try {
          if (!remoteReady) {
            pendingIce.push(msg.candidate);
          } else {
            await pc.addIceCandidate(msg.candidate);
          }
        } catch (_) {
          /* ignore */
        }
      }
      if (msg.type === "hangup") {
        setStatus("llamada cerrada");
      }
    };

    const videoEl = remoteRef.current;
    return () => {
      cancelled = true;
      clearInterval(keepalive);
      try {
        if (ws.readyState === 1) {
          ws.send(JSON.stringify({ type: "hangup" }));
        }
        ws.close();
      } catch (_) {
        /* ignore */
      }
      try {
        pc.getSenders().forEach(s => {
          try {
            s.track && s.track.stop();
          } catch (_) {
            /* ignore */
          }
        });
        pc.getReceivers().forEach(s => {
          try {
            s.track && s.track.stop();
          } catch (_) {
            /* ignore */
          }
        });
        pc.close();
      } catch (_) {
        /* ignore */
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
        localStreamRef.current = null;
      }
      if (videoEl) {
        const stream = videoEl.srcObject;
        if (stream && typeof stream.getTracks === "function") {
          stream.getTracks().forEach(t => t.stop());
        }
        videoEl.srcObject = null;
      }
    };
  }, [machineId]);

  const activateAudio = () => {
    const el = remoteRef.current;
    if (!el) return;
    el.muted = false;
    el.play().catch(() => {});
    setNeedClick(false);
  };

  return (
    <aside className="support-video-call is-live">
      <div className="support-video-call__head">
        <strong>
          {ticketId ? `Video del cliente · ticket #${ticketId}` : "Video del cliente"}
        </strong>
        <span>{error || status}</span>
      </div>
      <p className="support-video-call__hint">
        En vivo. Al colgar se cierra esta ventana y no queda imagen ni audio.
      </p>
      <div className="support-video-call__stage" onClick={activateAudio}>
        <video
          ref={remoteRef}
          className="support-video-call__video"
          autoPlay
          playsInline
          muted
          controls={false}
          disablePictureInPicture
          controlsList="nodownload noremoteplayback noplaybackrate"
          onContextMenu={ev => ev.preventDefault()}
        />
        {!hasRemote ? (
          <div className="support-video-call__overlay">Conectando con la máquina…</div>
        ) : null}
        {hasRemote && needClick ? (
          <div className="support-video-call__overlay support-video-call__overlay--cta">
            Tocá para oír al cliente
          </div>
        ) : null}
      </div>
    </aside>
  );
}
